import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';

const BASE_PADDING_X = 16;
const BASE_PADDING_Y = 14;
const BASE_BUTTON_FONT = 14;

const TypeAnswerElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentAnswer = '',
  showResult = false,
  isCorrect = null,
  onAnswerChange,
  onCheck,
  onRetry,
  disabled = false,
  scale = 1,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    question = '',
    correctAnswers = [],
    placeholder = t('games.typeAnswer.placeholder'),
    allowRetry = false,
    fontFamily = 'Inter',
    fontSize = 18,
    textColor = '#1f2937',
    inputTextColor = textColor,
    inputBgColor = '#ffffff',
    inputBorderColor = '#d1d5db',
    actionBarColor = '#f9fafb',
  } = content || {};

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  const containerRef = useRef(null);
  const [boxScale, setBoxScale] = useState(scale);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = (width, height) => {
      if (width <= 0 || height <= 0) return;
      const naturalWidth = 360;
      const naturalHeight = 160;
      const next = Math.min(width / naturalWidth, height / naturalHeight) * scale;
      setBoxScale(Math.max(0.3, next));
    };
    update(node.clientWidth, node.clientHeight);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        update(entry.contentRect.width, entry.contentRect.height);
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [scale]);

  const questionFontSize = fontSize;
  const inputFontSize = fontSize;
  const buttonFontSize = Math.max(10, Math.round(BASE_BUTTON_FONT * boxScale));
  const paddingX = Math.max(8, Math.round(BASE_PADDING_X * boxScale));
  const paddingY = Math.max(6, Math.round(BASE_PADDING_Y * boxScale));

  const [localAnswer, setLocalAnswer] = useState(currentAnswer || '');

  useEffect(() => {
    const external = currentAnswer || '';
    // Read-only viewers (teacher during a live lesson, editor preview, disabled
    // state, or after check) have no local edits to protect — always mirror the
    // latest external value so polled updates stay in sync.
    if (disabled || isEditor || showResult) {
      setLocalAnswer(external);
      return;
    }
    setLocalAnswer(prev => {
      // Student is actively typing: adopt external only on initial async load
      // (local still empty) or an external reset (external cleared). Keeping
      // local authoritative otherwise prevents stale prop echoes from wiping
      // recently typed characters when the student types faster than the
      // parent's state round-trip.
      if (!prev && external) return external;
      if (external === '' && !showResult) return '';
      return prev;
    });
  }, [currentAnswer, showResult, disabled, isEditor]);

  const handleInputChange = (e) => {
    if (disabled || showResult || isEditor) return;
    const v = e.target.value;
    setLocalAnswer(v);
    onAnswerChange?.(v);
  };

  const handleCheck = () => {
    if (!localAnswer.trim()) return;
    onCheck?.(localAnswer);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !showResult && localAnswer.trim()) {
      e.preventDefault();
      handleCheck();
    }
  };

  const handleRetry = () => {
    setLocalAnswer('');
    onRetry?.();
  };

  const isTransparent = backgroundColor === 'transparent';
  const hideChrome = !isEditor && isTransparent;

  const firstCorrect = Array.isArray(correctAnswers) && correctAnswers.length > 0
    ? correctAnswers[0]
    : '';

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col rounded-xl overflow-hidden ${
        hideChrome ? '' : 'border-2 border-gray-200 shadow-sm'
      }`}
      style={{ backgroundColor }}
    >
      {/* Question */}
      <div
        className="flex-1 min-h-0 flex flex-col overflow-hidden"
        style={{ paddingLeft: paddingX, paddingRight: paddingX, paddingTop: paddingY, paddingBottom: paddingY }}
      >
        {question && (
          <p
            className="font-medium mb-2"
            style={{ fontSize: questionFontSize, lineHeight: 1.3, fontFamily, color: textColor }}
          >
            {question}
          </p>
        )}

        {/* Input */}
        <textarea
          value={isEditor ? '' : localAnswer}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isEditor ? (firstCorrect || placeholder) : placeholder}
          disabled={isEditor || disabled || showResult}
          className="w-full flex-1 min-h-0 rounded-lg border-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400 transition disabled:bg-gray-50"
          style={{
            backgroundColor: showResult
              ? (isCorrect ? '#dcfce7' : '#fee2e2')
              : inputBgColor,
            borderColor: showResult
              ? (isCorrect ? '#22c55e' : '#ef4444')
              : inputBorderColor,
            color: inputTextColor,
            fontSize: inputFontSize,
            fontFamily,
            padding: `${Math.max(6, Math.round(8 * boxScale))}px ${Math.max(8, Math.round(12 * boxScale))}px`,
          }}
        />
      </div>

      {/* Actions */}
      {!isEditor && showResult && !isCorrect && allowRetry && (
        <div
          className={`flex items-center justify-end gap-2 ${
            isActionBarTransparent ? '' : 'border-t border-gray-100'
          }`}
          style={{
            backgroundColor: actionBarColor,
            paddingLeft: paddingX,
            paddingRight: paddingX,
            paddingTop: Math.max(4, Math.round(8 * boxScale)),
            paddingBottom: Math.max(4, Math.round(8 * boxScale)),
          }}
        >
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
            style={{ fontSize: buttonFontSize }}
          >
            <RotateCcw size={Math.max(12, Math.round(14 * boxScale))} />
            {t('games.common.tryAgain')}
          </button>
        </div>
      )}
    </div>
  );
};

export default TypeAnswerElement;
